/**
 * Authentication Error Handler Utility
 *
 * Provides context-aware error message extraction and normalization for authentication flows.
 * All error messages are passed through i18n with fallback to original message if no translation exists.
 */

import nl from '@/i18n/locales/nl.json';

/**
 * Backend error messages are full sentences (often ending in a period, e.g.
 * "We can't find a user with that email address."). vue-i18n's `t()` resolves
 * keys by splitting on `.`, so it can NEVER reach these as a leaf under
 * `response.error.*`. We therefore keep `t()` (covers translated/flat keys and
 * unit-test mocks) but fall back to a DIRECT lookup against the `response.error`
 * map. Per project policy these backend-message translations live in nl.json only.
 */
const BACKEND_ERROR_MAP = (nl && nl.response && nl.response.error) || {};

/**
 * Maps OAuth2 / Passport error CODES (the `error` field, e.g. `invalid_grant`)
 * to a localized, human-readable key. The login form talks straight to
 * Scrumble's Passport `oauth/token` endpoint, so a failed login can come back
 * as a League OAuth2 body (`{error, error_description, hint}`) with NO
 * `message`/`detail`/`errors` field. Without this map those would fall through
 * to the generic fallback. Only codes we can phrase meaningfully are listed;
 * anything else defers to `error_description` / the status-aware fallback.
 */
const OAUTH_ERROR_MESSAGE_KEYS = {
  invalid_grant: 'messages.invalid_credentials',
};

/**
 * Extract and normalize error messages from authentication API responses
 *
 * Guarantees a message that ALWAYS makes sense to a customer: never the bare
 * "error" label (which produced the nonsensical "Foutmelding: Foutmelding"),
 * and never a raw HTML/empty body from a crashing pod or proxy.
 *
 * @param {Object} error - Axios error object
 * @param {Function} t - i18n translate function
 * @param {string} authStep - Current authentication step: 'login' | 'sms_verification' | 'totp_verification' | 'email_verification'
 * @returns {string} Localized, context-aware error message
 */
export function extractAuthErrorMessage(error, t, authStep = 'login') {
  // No HTTP response at all: timeout, DNS failure, CORS, or the server being
  // unreachable mid-deploy. This is the most common "weird error" cause.
  if (!error.response) {
    return t('messages.network_error');
  }

  const status = error.response.status;
  const responseData = error.response.data;

  // Only mine structured fields from an OBJECT body. A 5xx from the ingress or
  // a restarting pod frequently returns an HTML page or empty string — reading
  // `.message` off that yields nothing and must never be surfaced raw.
  if (responseData && typeof responseData === 'object') {
    // 1. Field-specific errors based on the current auth step.
    const fieldSpecificMessage = extractFieldSpecificError(responseData, authStep);
    if (fieldSpecificMessage) {
      return translateErrorMessage(fieldSpecificMessage, t);
    }

    // 2. A general human-readable message from the API.
    const generalMessage = responseData.message || responseData.detail;
    if (generalMessage) {
      return translateErrorMessage(generalMessage, t);
    }

    // 3. Known OAuth2 error codes → a localized sentence (e.g. invalid_grant).
    const oauthKey = OAUTH_ERROR_MESSAGE_KEYS[responseData.error];
    if (oauthKey) {
      return t(oauthKey);
    }

    // 4. OAuth2 `error_description` as a last structured resort (English literal
    //    from the auth server; still far better than a generic fallback).
    if (responseData.error_description) {
      return translateErrorMessage(responseData.error_description, t);
    }
  }

  // 5. Status-aware fallback so the customer always sees a meaningful message.
  if (typeof status === 'number' && status >= 500) {
    return t('messages.server_error');
  }

  return t('messages.something_went_wrong');
}

/**
 * Extract field-specific error messages from the errors object
 * Prioritizes errors based on the current authentication step
 * 
 * @param {Object} responseData - API response data
 * @param {string} authStep - Current authentication step
 * @returns {string|null} Field-specific error message or null
 */
function extractFieldSpecificError(responseData, authStep) {
  const errors = responseData.errors;
  
  if (!errors || typeof errors !== 'object') {
    return null;
  }

  // Define field priority based on auth step
  const fieldPriority = {
    sms_verification: ['sms_2fa_code', 'two_factor_throttle', 'sms_2fa', 'email'],
    totp_verification: ['totp_code', 'recovery_code', 'two_factor_throttle', 'email'],
    email_verification: ['email_2fa_code', 'two_factor_throttle', 'email'],
    login: ['email', 'password', 'accept_tos', 'two_factor_throttle', 'sms_2fa'],
  };

  const fields = fieldPriority[authStep] || fieldPriority.login;

  // Try each field in priority order
  for (const field of fields) {
    const fieldError = errors[field];
    
    if (fieldError) {
      // Handle both array and string formats
      if (Array.isArray(fieldError) && fieldError.length > 0) {
        return fieldError[0];
      } else if (typeof fieldError === 'string') {
        return fieldError;
      }
    }
  }

  // If no prioritized field found, try to get first available error.
  // `two_factor` carries machine-readable method names (['totp','sms']),
  // not a human-readable message — never surface it as error text.
  const firstErrorKey = Object.keys(errors).filter((key) => key !== 'two_factor')[0];
  if (firstErrorKey) {
    const firstError = errors[firstErrorKey];
    if (Array.isArray(firstError) && firstError.length > 0) {
      return firstError[0];
    } else if (typeof firstError === 'string') {
      return firstError;
    }
  }

  return null;
}

/**
 * Translate error message using i18n with fallback
 * Uses the pattern: t(`response.error.${message}`, message)
 * 
 * Translation Strategy:
 * - Backend may send messages in various languages (typically Dutch or English)
 * - Locale files (nl.json, en.json, etc.) map backend messages to localized versions
 * - For nl.json, backend Dutch messages often map to themselves (e.g., 'Ongeldige...' → 'Ongeldige...')
 * - For en.json, Dutch backend messages would map to English translations
 * - If no translation key exists, the original message is shown (fallback parameter)
 * - This ensures users always see an error message, even for unexpected backend errors
 * 
 * @param {string} message - Error message to translate
 * @param {Function} t - i18n translate function
 * @returns {string} Translated message or original if no translation exists
 */
function translateErrorMessage(message, t) {
  if (!message) {
    return t('messages.error_title');
  }

  // Try to translate using response.error prefix. With a real vue-i18n this
  // only resolves "flat" (dot-free) keys; unit-test mocks resolve it directly.
  const translationKey = `response.error.${message}`;
  const translated = t(translationKey, message);
  if (translated && translated !== message) {
    return translated;
  }

  // Fallback: direct map lookup. This is what actually resolves the common
  // dotted backend sentences (e.g. "We can't find a user with that email
  // address.") that vue-i18n's dotted-path lookup silently misses.
  return BACKEND_ERROR_MAP[message] || message;
}
